import React from 'react';
import { PlusCircle, ClipboardList, CheckCircle2, Clock, AlertCircle, Trash2, Plane, Home } from 'lucide-react';
import { Country, Task } from '../types/todo';

interface CountryCardProps {
  country: Country;
  onCreateList: (code: string) => void;
  onManageList: (code: string) => void;
  onDeleteList?: (code: string, fromDashboard: boolean) => void;
  tasks?: Task[];
  departureDate?: string;
  returnDate?: string;
  createdAt?: any;
}

export function CountryCard({
  country,
  onCreateList,
  onManageList,
  onDeleteList,
  tasks = [],
  departureDate,
  returnDate
}: CountryCardProps) {
  const handleClick = (action: () => void) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(action, 100);
  };

  const stats = React.useMemo(() => {
    if (!tasks.length) return null;

    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in-progress').length;
    const notStarted = total - completed - inProgress;
    const progress = Math.round((completed / total) * 100);

    return { total, completed, inProgress, notStarted, progress };
  }, [tasks]);

  const hasChecklist = tasks.length > 0;

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200/60 dark:border-gray-700/50 hover:shadow-lg transition-shadow duration-300">
      <div className="relative h-36 sm:h-48 overflow-hidden">
        <img
          src={country.image}
          alt={country.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/60" />

        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-3xl sm:text-4xl">{country.flag}</span>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">{country.name}</h3>
                <p className="text-[11px] sm:text-xs text-white/80 font-medium">Programme Vacances-Travail</p>
              </div>
            </div>

            {hasChecklist && onDeleteList && (
              <button
                onClick={() => onDeleteList(country.code, true)}
                className="p-2 text-white/70 hover:text-red-400 rounded-lg bg-white/10 hover:bg-red-500/20 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
        {hasChecklist && stats && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-600 dark:text-gray-400">Progression</span>
                <span className="font-bold text-gray-900 dark:text-white">{stats.progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500 rounded-full"
                  style={{ width: `${stats.progress}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                <span>{stats.completed}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                <span>{stats.inProgress}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-gray-400" />
                <span>{stats.notStarted}</span>
              </div>
            </div>
          </>
        )}

        {departureDate && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
            <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Plane className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">Depart</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white">
                {new Date(departureDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>
            </div>
            {returnDate && (
              <>
                <div className="w-px h-8 bg-gray-200 dark:bg-gray-600" />
                <div className="p-1.5 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                  <Home className="w-3.5 h-3.5 text-cyan-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">Retour</p>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {new Date(returnDate).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {hasChecklist ? (
          <button
            onClick={() => handleClick(() => onManageList(country.code))}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white font-medium rounded-xl transition-colors text-sm"
          >
            <ClipboardList className="w-4 h-4" />
            <span>Gerer ma checklist</span>
          </button>
        ) : (
          <button
            onClick={() => handleClick(() => onCreateList(country.code))}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors text-sm shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Creer ma checklist</span>
          </button>
        )}
      </div>
    </div>
  );
}
